(function () {
  'use strict';

  const MENU_ID = 'frankos-category-mega';
  const SCRIPT_VERSION = 'v21.0-dynamic-shopify-navigation';
  const PAGE_PUSH_CLASS = 'frankos-category-mega-page-open';
  const PAGE_PUSH_VARIABLE = '--frankos-category-mega-panel-space';

  let globalEventsBound = false;
  let hydrationObserver = null;
  let hydrationTimer = null;
  let flyoutRetryTimer = null;
  let flyoutRetryAttempt = 0;
  let scrollFrame = null;

  const REQUIRED_HEADER_FLYOUTS = 5;
  const MAX_FLYOUT_RETRY_ATTEMPTS = 24;

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function getMegaMenu() {
    return document.getElementById(MENU_ID);
  }

  function getCategoryIdByLabel(label) {
    const menu = getMegaMenu();
    if (!menu) return null;

    const normalized = normalizeText(label);
    const fallbackNormalized =
      normalized === 'meer' ? 'diversen' : normalized;

    const links = Array.from(
      menu.querySelectorAll('[data-frankos-category]')
    );

    const match = links.find((link) => {
      const title = normalizeText(link.textContent);
      return title === fallbackNormalized;
    });

    return match ? match.dataset.frankosCategory : null;
  }

  function getCategoryData(categoryId) {
    const menu = getMegaMenu();
    if (!menu || !categoryId) return null;

    const safeId = escapeSelector(categoryId);

    const mainLink = menu.querySelector(
      `[data-frankos-category="${safeId}"]`
    );

    const content = menu.querySelector(
      `[data-frankos-category-content="${safeId}"]`
    );

    if (!mainLink || !content) return null;

    const titleElement = content.querySelector('h2');

    const viewAll = content.querySelector(
      '.frankos-category-mega__view-all'
    );

    const groups = Array.from(
      content.querySelectorAll('.frankos-category-mega__group')
    ).map((group) => {
      const groupTitle = group.querySelector(
        '.frankos-category-mega__group-name'
      );

      const groupTitleLink = group.querySelector(
        '.frankos-category-mega__group-title[href]'
      );

      const links = Array.from(
        group.querySelectorAll('.frankos-category-mega__link')
      ).map((link) => {
        const linkText = link.querySelector(
          '.frankos-category-mega__link-text'
        );

        return {
          label: linkText
            ? linkText.textContent.trim()
            : link.textContent.trim(),
          href: link.getAttribute('href') || '#'
        };
      });

      return {
        title: groupTitle
          ? groupTitle.textContent.trim()
          : '',
        href: groupTitleLink
          ? groupTitleLink.getAttribute('href') || ''
          : '',
        links
      };
    });

    return {
      id: categoryId,

      title: titleElement
        ? titleElement.textContent.trim()
        : mainLink.textContent.trim(),

      href: viewAll
        ? viewAll.getAttribute('href')
        : mainLink.getAttribute('href') || '#',

      groups
    };
  }

  function createLink(link) {
    return `
      <a
        class="frankos-menu-flyout__link"
        href="${escapeHtml(link.href)}"
      >
        <span class="frankos-menu-flyout__link-text">
          ${escapeHtml(link.label)}
        </span>
      </a>
    `;
  }

  function createFlyoutGroup(group) {
    const groupHeading = group.href
      ? `
        <a
          class="frankos-menu-flyout__group-title frankos-menu-flyout__group-link"
          href="${escapeHtml(group.href)}"
          aria-label="Bekijk alle ${escapeHtml(group.title)}"
        >
          <span class="frankos-menu-flyout__group-name">
            ${escapeHtml(group.title)}
          </span>
        </a>
      `
      : `
        <div
          class="frankos-menu-flyout__group-title"
          role="presentation"
          aria-label="Subcategorie ${escapeHtml(group.title)}"
        >
          <span class="frankos-menu-flyout__group-name">
            ${escapeHtml(group.title)}
          </span>
        </div>
      `;

    return `
      <div class="frankos-menu-flyout__group">
        ${groupHeading}

        <div class="frankos-menu-flyout__links">
          ${group.links.map(createLink).join('')}
        </div>
      </div>
    `;
  }

  function createFlyout(category) {
    return `
      <div
        class="frankos-menu-flyout"
        data-frankos-menu-flyout="${escapeHtml(category.id)}"
        data-frankos-version="${SCRIPT_VERSION}"
      >
        <div class="frankos-menu-flyout__head">
          <div>
            <p class="frankos-menu-flyout__eyebrow">
              FrankOs categorie
            </p>

            <h2 class="frankos-menu-flyout__title">
              ${escapeHtml(category.title)}
            </h2>
          </div>

          <a
            class="frankos-menu-flyout__view-all"
            href="${escapeHtml(category.href)}"
          >
            Bekijk alles
          </a>
        </div>

        <div class="frankos-menu-flyout__groups">
          ${category.groups.map(createFlyoutGroup).join('')}
        </div>
      </div>
    `;
  }

  function setActiveCategory(menu, categoryId) {
    menu
      .querySelectorAll('[data-frankos-category]')
      .forEach((item) => {
        item.classList.toggle(
          'is-active',
          item.dataset.frankosCategory === categoryId
        );
      });

    menu
      .querySelectorAll('[data-frankos-category-content]')
      .forEach((content) => {
        content.classList.toggle(
          'is-active',
          content.dataset.frankosCategoryContent === categoryId
        );
      });
  }

  function updateMenuPageSpace(menu) {
    if (
      !menu ||
      !menu.classList.contains('is-open')
    ) {
      return;
    }

    const panel = menu.querySelector(
      '.frankos-category-mega__panel'
    );

    if (!panel) return;

    window.requestAnimationFrame(() => {
      if (!menu.classList.contains('is-open')) return;

      const panelHeight = Math.ceil(
        panel.getBoundingClientRect().height
      );

      const reservedSpace = Math.max(
        0,
        panelHeight + 14
      );

      document.documentElement.style.setProperty(
        PAGE_PUSH_VARIABLE,
        `${reservedSpace}px`
      );

      document.documentElement.classList.add(
        PAGE_PUSH_CLASS
      );
    });
  }

  function updateMegaPanelViewport(menu) {
    if (
      !menu ||
      !menu.classList.contains('is-open')
    ) {
      return;
    }

    const panel = menu.querySelector(
      '.frankos-category-mega__panel'
    );

    if (!panel) return;

    window.requestAnimationFrame(() => {
      if (!menu.classList.contains('is-open')) return;

      const rect = panel.getBoundingClientRect();

      const available = Math.max(
        1,
        Math.floor(
          window.innerHeight - rect.top - 18
        )
      );

      panel.style.setProperty(
        '--frankos-mega-available-height',
        `${available}px`
      );

      updateMenuPageSpace(menu);
    });
  }

  function clearMenuPageSpace() {
    document.documentElement.classList.remove(
      PAGE_PUSH_CLASS
    );

    document.documentElement.style.removeProperty(
      PAGE_PUSH_VARIABLE
    );
  }

  function openMenu(menu) {
    if (!menu) return;

    window.clearTimeout(
      menu._frankosCloseTimer
    );

    menu.classList.add('is-open');

    document.documentElement.classList.add(
      PAGE_PUSH_CLASS
    );

    const button = menu.querySelector(
      '.frankos-category-mega__button'
    );

    if (button) {
      button.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    updateMegaPanelViewport(menu);
  }

  function closeMenu(menu, delay = 180) {
    if (!menu) return;

    window.clearTimeout(
      menu._frankosCloseTimer
    );

    menu._frankosCloseTimer =
      window.setTimeout(() => {
        menu.classList.remove('is-open');

        clearMenuPageSpace();

        const button = menu.querySelector(
          '.frankos-category-mega__button'
        );

        if (button) {
          button.setAttribute(
            'aria-expanded',
            'false'
          );
        }
      }, delay);
  }

  function closeAllFlyouts(exceptItem) {
    document
      .querySelectorAll(
        '#header-component ' +
          '.menu-list__list-item.frankos-flyout-open'
      )
      .forEach((item) => {
        if (
          exceptItem &&
          item === exceptItem
        ) {
          return;
        }

        window.clearTimeout(
          item._frankosFlyoutTimer
        );

        item.classList.remove(
          'frankos-flyout-open'
        );
      });
  }

  function updateFlyoutMaxHeight(flyout) {
    if (!flyout) return;

    flyout.style.removeProperty('position');
    flyout.style.removeProperty('top');
    flyout.style.removeProperty('left');
    flyout.style.removeProperty('max-height');
  }

  function bindMegaMenu(menu) {
    if (
      !menu ||
      menu.dataset.frankosBound === 'true'
    ) {
      return;
    }

    menu.dataset.frankosBound = 'true';

    const button = menu.querySelector(
      '.frankos-category-mega__button'
    );

    menu
      .querySelectorAll('[data-frankos-category]')
      .forEach((item) => {
        item.addEventListener(
          'mouseenter',
          () => {
            setActiveCategory(
              menu,
              item.dataset.frankosCategory
            );

            openMenu(menu);
            closeAllFlyouts();
          }
        );

        item.addEventListener(
          'focus',
          () => {
            setActiveCategory(
              menu,
              item.dataset.frankosCategory
            );

            openMenu(menu);
            closeAllFlyouts();
          }
        );
      });

    menu.addEventListener(
      'mouseenter',
      () => {
        openMenu(menu);
        closeAllFlyouts();
      }
    );

    menu.addEventListener(
      'mouseleave',
      () => closeMenu(menu, 160)
    );

    if (button) {
      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          if (
            menu.classList.contains('is-open')
          ) {
            closeMenu(menu, 0);
          } else {
            openMenu(menu);
            closeAllFlyouts();
          }
        }
      );

      button.addEventListener(
        'focus',
        () => {
          openMenu(menu);
          closeAllFlyouts();
        }
      );
    }
  }

  function attachFlyoutToListItem(
    listItem,
    category
  ) {
    if (!listItem || !category) return false;

    const existing = listItem.querySelector(
      ':scope > .frankos-menu-flyout'
    );

    if (
      existing &&
      existing.dataset.frankosVersion ===
        SCRIPT_VERSION
    ) {
      listItem.classList.add(
        'frankos-menu-has-flyout'
      );

      listItem.dataset.frankosFlyoutReady =
        'true';

      return true;
    }

    if (existing) {
      existing.remove();
    }

    listItem.classList.add(
      'frankos-menu-has-flyout'
    );

    listItem.dataset.frankosFlyoutReady =
      'true';

    listItem.insertAdjacentHTML(
      'beforeend',
      createFlyout(category)
    );

    return true;
  }

  function getHeaderListItemTitle(
    listItem
  ) {
    if (!listItem) return '';

    const titleElement =
      listItem.querySelector(
        ':scope > .menu-list__link ' +
          '.menu-list__link-title, ' +
          ':scope > button ' +
          '.menu-list__link-title, ' +
          '.menu-list__link-title'
      );

    return titleElement
      ? titleElement.textContent
      : listItem.textContent;
  }

  function ensureFlyoutForListItem(
    listItem
  ) {
    if (
      !listItem ||
      !listItem.closest(
        '#header-component ' +
          'header-menu.header-menu'
      )
    ) {
      return false;
    }

    const label =
      getHeaderListItemTitle(listItem);

    const categoryId =
      getCategoryIdByLabel(label);

    if (!categoryId) return false;

    const category =
      getCategoryData(categoryId);

    if (!category) return false;

    return attachFlyoutToListItem(
      listItem,
      category
    );
  }

  function openHeaderFlyout(listItem) {
    if (
      !listItem ||
      !ensureFlyoutForListItem(listItem)
    ) {
      return;
    }

    const menu = getMegaMenu();

    closeMenu(menu, 0);
    closeAllFlyouts(listItem);

    window.clearTimeout(
      listItem._frankosFlyoutTimer
    );

    listItem.classList.add(
      'frankos-flyout-open'
    );

    updateFlyoutMaxHeight(
      listItem.querySelector(
        ':scope > .frankos-menu-flyout'
      )
    );
  }

  function closeHeaderFlyout(
    listItem,
    delay = 80
  ) {
    if (!listItem) return;

    window.clearTimeout(
      listItem._frankosFlyoutTimer
    );

    listItem._frankosFlyoutTimer =
      window.setTimeout(() => {
        listItem.classList.remove(
          'frankos-flyout-open'
        );
      }, delay);
  }

  function attachHeaderMenuFlyouts() {
    const headerMenus = Array.from(
      document.querySelectorAll(
        '#header-component ' +
          'header-menu.header-menu'
      )
    );

    if (
      headerMenus.length === 0 ||
      !getMegaMenu()
    ) {
      return 0;
    }

    let attachedCount = 0;

    headerMenus.forEach((headerMenu) => {
      const listItems = Array.from(
        headerMenu.querySelectorAll(
          '.menu-list__list-item'
        )
      );

      listItems.forEach((listItem) => {
        if (
          ensureFlyoutForListItem(listItem)
        ) {
          attachedCount += 1;
        }
      });
    });

    return attachedCount;
  }

  function scheduleFlyoutRetry() {
    window.clearTimeout(
      flyoutRetryTimer
    );

    if (
      flyoutRetryAttempt >=
      MAX_FLYOUT_RETRY_ATTEMPTS
    ) {
      flyoutRetryAttempt = 0;
      return;
    }

    const delay = Math.min(
      60 + flyoutRetryAttempt * 35,
      500
    );

    flyoutRetryAttempt += 1;

    flyoutRetryTimer =
      window.setTimeout(() => {
        const attachedCount =
          attachHeaderMenuFlyouts();

        if (
          attachedCount <
          REQUIRED_HEADER_FLYOUTS
        ) {
          scheduleFlyoutRetry();
        } else {
          flyoutRetryAttempt = 0;
        }
      }, delay);
  }

  function updateOpenFlyouts() {
    document
      .querySelectorAll(
        '#header-component ' +
          '.menu-list__list-item.' +
          'frankos-flyout-open > ' +
          '.frankos-menu-flyout'
      )
      .forEach(updateFlyoutMaxHeight);
  }

  function init() {
    const menu = getMegaMenu();

    bindMegaMenu(menu);

    const attachedCount =
      attachHeaderMenuFlyouts();

    if (
      attachedCount <
      REQUIRED_HEADER_FLYOUTS
    ) {
      scheduleFlyoutRetry();
    } else {
      flyoutRetryAttempt = 0;
    }

    document.documentElement.classList.add(
      'frankos-category-mega-ready'
    );

    return Boolean(menu);
  }

  function scheduleInit() {
    window.clearTimeout(
      hydrationTimer
    );

    hydrationTimer = window.setTimeout(
      init,
      40
    );
  }

  function observeHeaderHydration() {
    if (hydrationObserver) return;

    const headerGroup =
      document.getElementById(
        'header-group'
      );

    if (!headerGroup) return;

    hydrationObserver =
      new MutationObserver(
        scheduleInit
      );

    hydrationObserver.observe(
      headerGroup,
      {
        childList: true,
        subtree: true
      }
    );
  }

  function bindGlobalEvents() {
    if (globalEventsBound) return;

    globalEventsBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const menu = getMegaMenu();

        if (
          menu &&
          !menu.contains(event.target)
        ) {
          closeMenu(menu, 0);
        }
      }
    );

    /*
      Gedelegeerde flyout-events:
      blijven werken wanneer Horizon menu-items vervangt of hydrateert.
    */
    document.addEventListener(
      'pointerover',
      (event) => {
        if (
          event.pointerType &&
          event.pointerType !== 'mouse' &&
          event.pointerType !== 'pen'
        ) {
          return;
        }

        const listItem =
          event.target.closest?.(
            '#header-component ' +
              'header-menu.header-menu ' +
              '.menu-list__list-item'
          );

        if (!listItem) return;

        if (
          event.relatedTarget &&
          listItem.contains(
            event.relatedTarget
          )
        ) {
          return;
        }

        openHeaderFlyout(listItem);
      },
      true
    );

    document.addEventListener(
      'pointerout',
      (event) => {
        if (
          event.pointerType &&
          event.pointerType !== 'mouse' &&
          event.pointerType !== 'pen'
        ) {
          return;
        }

        const listItem =
          event.target.closest?.(
            '#header-component ' +
              'header-menu.header-menu ' +
              '.menu-list__list-item.' +
              'frankos-menu-has-flyout'
          );

        if (!listItem) return;

        if (
          event.relatedTarget &&
          listItem.contains(
            event.relatedTarget
          )
        ) {
          return;
        }

        closeHeaderFlyout(
          listItem,
          80
        );
      },
      true
    );

    document.addEventListener(
      'focusin',
      (event) => {
        const listItem =
          event.target.closest?.(
            '#header-component ' +
              'header-menu.header-menu ' +
              '.menu-list__list-item'
          );

        if (!listItem) return;

        openHeaderFlyout(listItem);
      },
      true
    );

    document.addEventListener(
      'focusout',
      (event) => {
        const listItem =
          event.target.closest?.(
            '#header-component ' +
              'header-menu.header-menu ' +
              '.menu-list__list-item.' +
              'frankos-menu-has-flyout'
          );

        if (!listItem) return;

        window.setTimeout(() => {
          if (
            !listItem.contains(
              document.activeElement
            )
          ) {
            closeHeaderFlyout(
              listItem,
              0
            );
          }
        }, 0);
      },
      true
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;

        closeMenu(
          getMegaMenu(),
          0
        );

        closeAllFlyouts();
      }
    );

    document.addEventListener(
      'shopify:section:load',
      scheduleInit
    );

    window.addEventListener(
      'pageshow',
      scheduleInit
    );

    window.addEventListener(
      'resize',
      () => {
        const menu = getMegaMenu();

        if (
          menu &&
          menu.classList.contains('is-open')
        ) {
          updateMegaPanelViewport(menu);
        }

        updateOpenFlyouts();
      }
    );

    window.addEventListener(
      'scroll',
      () => {
        if (scrollFrame !== null) return;

        scrollFrame =
          window.requestAnimationFrame(
            () => {
              scrollFrame = null;

              const menu =
                getMegaMenu();

              if (
                menu &&
                menu.classList.contains(
                  'is-open'
                )
              ) {
                updateMegaPanelViewport(
                  menu
                );
              }

              updateOpenFlyouts();
            }
          );
      },
      {
        passive: true
      }
    );
  }

  function boot() {
    init();
    bindGlobalEvents();
    observeHeaderHydration();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  window.FrankOsCategoryMegaMenu = {
    version: SCRIPT_VERSION,
    init,
    closeAllFlyouts
  };
})();